import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../i18n";
import { trackNotificationShown } from "../utils/analytics";
import BuyUnitsModal from "./BuyUnitsModal";
import "./Navbar.css";

function Navbar() {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { locale, setLocale, t } = useI18n();

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [buyUnitsOpen, setBuyUnitsOpen] = useState(false);
  const [notificationCounts, setNotificationCounts] = useState({
    unseenMatches: 0,
    unreadMessages: 0
  });

  async function fetchNotificationCounts() {
    if (!isAuthenticated) {
      setNotificationCounts({
        unseenMatches: 0,
        unreadMessages: 0
      });
      return;
    }

    const token = localStorage.getItem("token");

    if (!token) {
      return;
    }

    try {
      const response = await axios.get("/api/notifications/counts", {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      setNotificationCounts({
        unseenMatches: response.data?.unseenMatches ?? 0,
        unreadMessages: response.data?.unreadMessages ?? 0
      });
    } catch (err) {
      console.error("Notification counts error:", err);
    }
  }

  useEffect(() => {
    let cancelled = false;

    async function loadCounts() {
      if (cancelled) {
        return;
      }

      await fetchNotificationCounts();
    }

    loadCounts();

    const intervalId = setInterval(loadCounts, 30000);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [isAuthenticated]);

  /**
   * Refresh notification counts whenever route changes.
   *
   * This is important because:
   * - opening /matches calls /api/matches/mark-seen inside Matches.js
   * - then Navbar should refresh and lower the Matches counter
   */
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const timeoutId = setTimeout(() => {
      fetchNotificationCounts();
    }, 600);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [location.pathname, isAuthenticated]);

  useEffect(() => {
    if (notificationCounts.unseenMatches > 0) {
      trackNotificationShown("matches", notificationCounts.unseenMatches);
    }
    if (notificationCounts.unreadMessages > 0) {
      trackNotificationShown("messages", notificationCounts.unreadMessages);
    }
  }, [notificationCounts.unseenMatches, notificationCounts.unreadMessages]);

  async function handleLogout() {
    await logout();
    setDropdownOpen(false);
    setBuyUnitsOpen(false);
    navigate("/login");
  }

  function getInitial(username) {
    if (!username) {
      return "?";
    }

    return username.charAt(0).toUpperCase();
  }

  function handleOpenBuyUnits() {
    setDropdownOpen(false);
    setBuyUnitsOpen(true);
  }

  function handleCloseBuyUnits() {
    setBuyUnitsOpen(false);
  }

  function closeDropdown() {
    setDropdownOpen(false);
  }

  function handleNavItemClick(path, event) {
    const isCurrentPage =
      path === "/"
        ? location.pathname === "/"
        : location.pathname === path ||
          location.pathname.startsWith(`${path}/`);

    if (!isCurrentPage) {
      return;
    }

    event.preventDefault();
    window.scrollTo({ top: 0, behavior: "smooth" });

    if (path === "/messages") {
      document.querySelectorAll(".messages-panel").forEach((panel) => {
        panel.scrollTo({ top: 0, behavior: "smooth" });
      });
    }
  }

  function formatBadgeCount(count) {
    if (!count || count <= 0) {
      return "";
    }

    return count > 99 ? "99+" : count;
  }

  return (
    <>
      <nav className="navbar">
        <Link to="/" className="navbar-brand">
          <span className="brand-icon">♥</span>
          <span>TajneMysli</span>
        </Link>

        <div className="navbar-links">
          <NavLink
            to="/"
            end
            onClick={(event) => handleNavItemClick("/", event)}
            className={({ isActive }) =>
              isActive ? "nav-item active" : "nav-item"
            }
          >
            <span className="nav-emoji">⌂</span>
            <span>{t("nav.home")}</span>
          </NavLink>

          {isAuthenticated && (
            <>
              <NavLink
                to="/browse"
                onClick={(event) => handleNavItemClick("/browse", event)}
                className={({ isActive }) =>
                  isActive ? "nav-item active" : "nav-item"
                }
              >
                <span className="nav-icon-wrap">
                  <span className="nav-emoji">●</span>
                </span>
                <span>{t("nav.browse")}</span>
              </NavLink>

              <NavLink
                to="/matches"
                onClick={(event) => handleNavItemClick("/matches", event)}
                className={({ isActive }) =>
                  isActive ? "nav-item active" : "nav-item"
                }
              >
                <span className="nav-icon-wrap">
                  <span className="nav-emoji nav-heart-icon">♥</span>

                  {notificationCounts.unseenMatches > 0 && (
                    <span className="nav-badge nav-badge-alert nav-badge-pulse">
                      {formatBadgeCount(notificationCounts.unseenMatches)}
                    </span>
                  )}
                </span>

                <span>{t("nav.matches")}</span>
              </NavLink>

              <NavLink
                to="/messages"
                end
                onClick={(event) => handleNavItemClick("/messages", event)}
                className={({ isActive }) =>
                  isActive ? "nav-item active" : "nav-item"
                }
              >
                <span className="nav-icon-wrap">
                  <span className="nav-emoji nav-envelope-icon">
                    <svg viewBox="0 0 24 24" width="1em" height="1em" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="5" width="18" height="14" rx="2" />
                      <path d="M3 7l9 6 9-6" />
                    </svg>
                  </span>

                  {notificationCounts.unreadMessages > 0 && (
                    <span className="nav-badge nav-badge-alert nav-badge-shake">
                      {formatBadgeCount(notificationCounts.unreadMessages)}
                    </span>
                  )}
                </span>

                <span>{t("nav.messages")}</span>
              </NavLink>
            </>
          )}

          {!isAuthenticated && (
            <>
              <NavLink
                to="/register"
                className={({ isActive }) =>
                  isActive ? "nav-item active" : "nav-item"
                }
              >
                <span className="nav-emoji">＋</span>
                <span>{t("nav.register")}</span>
              </NavLink>

              <NavLink
                to="/login"
                className={({ isActive }) =>
                  isActive ? "nav-item active" : "nav-item"
                }
              >
                <span className="nav-emoji">
                  <svg viewBox="0 0 24 24" width="1em" height="1em" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
                    <path d="M10 17l5-5-5-5" />
                    <path d="M15 12H3" />
                  </svg>
                </span>
                <span>{t("nav.login")}</span>
              </NavLink>
            </>
          )}
        </div>

        {isAuthenticated && (
          <div className="navbar-right">
            <button
              type="button"
              className="buy-units-nav-button"
              onClick={handleOpenBuyUnits}
            >
              💰 {t("nav.buyUnits")}
            </button>

            <div
              className="profile-chip"
              onClick={() => setDropdownOpen(!dropdownOpen)}
            >
              <div className="avatar">{getInitial(user?.username)}</div>

              <span className="username">{user?.username}</span>

              <span className="dropdown-arrow">▾</span>
            </div>

            <div
              className={
                dropdownOpen ? "profile-dropdown open" : "profile-dropdown"
              }
            >
              <div className="dropdown-balance">
                <span className="dropdown-balance-label">{t("nav.balance")}</span>
                <span className="dropdown-balance-value">
                  {user?.tokenBalance ?? 0} {t("nav.units")}
                </span>
              </div>

              <div className="dropdown-divider"></div>

              <Link
                to="/profile"
                className="dropdown-item"
                onClick={closeDropdown}
              >
                <span>👤</span>
                <span>{t("nav.profile")}</span>
              </Link>

              <Link
                to="/matches"
                className="dropdown-item"
                onClick={closeDropdown}
              >
                <span>💕</span>
                <span>{t("nav.matches")}</span>

                {notificationCounts.unseenMatches > 0 && (
                  <span className="dropdown-count">
                    {formatBadgeCount(notificationCounts.unseenMatches)}
                  </span>
                )}
              </Link>

              <Link to="/payments" className="dropdown-item" onClick={closeDropdown}><span>💳</span><span>{t("nav.paymentHistory")}</span></Link>

              <Link
                to="/messages"
                className="dropdown-item"
                onClick={closeDropdown}
              >
                <span>✉️</span>
                <span>{t("nav.messages")}</span>

                {notificationCounts.unreadMessages > 0 && (
                  <span className="dropdown-count">
                    {formatBadgeCount(notificationCounts.unreadMessages)}
                  </span>
                )}
              </Link>

              <button
                type="button"
                className="dropdown-item"
                onClick={handleOpenBuyUnits}
              >
                <span>💰</span>
                <span>{t("nav.buyUnits")}</span>
              </button>

              <div className="dropdown-divider"></div>

              <button
                type="button"
                className="dropdown-item dropdown-logout"
                onClick={handleLogout}
              >
                <span>🚪</span>
                <span>{t("nav.logout")}</span>
              </button>
            </div>
          </div>
        )}
        <label className="navbar-language">
          <span className="sr-only">{t("language")}</span>
          <select value={locale} onChange={(event) => setLocale(event.target.value)} aria-label={t("language")}>
            <option value="en">{t("english")}</option>
            <option value="pl">{t("polish")}</option>
          </select>
        </label>
      </nav>

      {isAuthenticated && (
        <BuyUnitsModal
          isOpen={buyUnitsOpen}
          onClose={handleCloseBuyUnits}
        />
      )}
    </>
  );
}

export default Navbar;
